import {
  Badge,
  Caption1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { PeopleImportView, PeopleRowRecord } from '@lg/core';
import { SEED_ROLE_CATALOG } from '@lg/shared';
import { usd } from '../ui/format';

/** D365 security role identifier → its name, for the role badges. */
const ROLE_NAME = new Map(SEED_ROLE_CATALOG.map((r) => [r.roleId, r.name]));

const useStyles = makeStyles({
  chain: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  link: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  wrap: { overflowX: 'auto' },
  table: { tableLayout: 'auto', width: '100%' },
  nowrap: { whiteSpace: 'nowrap' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  sub: { display: 'block', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  row: { cursor: 'pointer' },
  roles: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  missing: { color: tokens.colorPaletteRedForeground1 },
});

const s = (r: PeopleRowRecord | undefined, k: string): string => {
  const v = r?.data[k];
  return v === undefined || v === null ? '' : String(v);
};
const lower = (v: string): string => v.trim().toLowerCase();
const upper = (v: string): string => v.trim().toUpperCase();

/**
 * One row per seat, joining the six sheets so the links are visible:
 * user (UPN) ↔ worker (personnel number) ↔ assignment ↔ position → job → limit, plus roles.
 */
export function WhoIsWho({
  view,
  onOpen,
}: {
  view: PeopleImportView;
  onOpen: (row: PeopleRowRecord) => void;
}) {
  const styles = useStyles();
  const of = (sheet: PeopleRowRecord['sheet']) => view.rows.filter((r) => r.sheet === sheet);
  const users = of('users');
  const workers = of('workers');
  const positions = of('positions');
  const assignments = of('assignments');
  const limits = of('approvalLimits');
  const roles = of('roles');

  const workerFor = (positionId: string) => {
    const a = assignments.find((x) => upper(s(x, 'positionId')) === positionId);
    return a
      ? workers.find((w) => upper(s(w, 'personnelNumber')) === upper(s(a, 'personnelNumber')))
      : undefined;
  };
  const nameOf = (w: PeopleRowRecord | undefined) =>
    w ? `${s(w, 'firstName')} ${s(w, 'lastName')}`.trim() : '';
  const poLimit = (jobId: string) => {
    const l = limits.find((x) => upper(s(x, 'jobId')) === jobId && s(x, 'documentType') === 'PurchaseOrder');
    return l ? Number(s(l, 'amountUsd').replace(/[$,\s]/g, '')) : null;
  };

  const seated = new Set<string>();
  const rows = positions.map((p) => {
    const positionId = upper(s(p, 'positionId'));
    const w = workerFor(positionId);
    if (w) seated.add(upper(s(w, 'personnelNumber')));
    const upn = lower(s(w, 'upn'));
    const reportsTo = upper(s(p, 'reportsToPositionId'));
    const boss = positions.find((x) => upper(s(x, 'positionId')) === reportsTo);
    return {
      key: p.id,
      open: p,
      positionId,
      title: s(p, 'title'),
      reportsTo,
      reportsToName: boss ? nameOf(workerFor(reportsTo)) || 'vacant' : 'outside the venue (D365)',
      jobId: upper(s(p, 'jobId')),
      limit: poLimit(upper(s(p, 'jobId'))),
      person: nameOf(w),
      personnelNumber: s(w, 'personnelNumber'),
      upn,
      hasUser: !!upn && users.some((u) => lower(s(u, 'upn')) === upn),
      roleIds: upn ? roles.filter((r) => lower(s(r, 'upn')) === upn).map((r) => s(r, 'roleId')) : [],
    };
  });
  const unseated = workers.filter((w) => !seated.has(upper(s(w, 'personnelNumber'))));

  return (
    <>
      <div className={styles.chain} aria-label="How the sheets link">
        <span className={styles.link}>User</span> UPN <span>↔</span>
        <span className={styles.link}>Worker</span> personnel no. <span>→</span>
        <span className={styles.link}>Assignment</span> <span>→</span>
        <span className={styles.link}>Position</span> job <span>→</span>
        <span className={styles.link}>Approval limit</span>
        <span>· roles attach to the</span> <span className={styles.link}>User</span>
      </div>
      <div className={styles.wrap}>
        <Table size="small" className={styles.table} aria-label="Who's who">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Position</TableHeaderCell>
              <TableHeaderCell>Person</TableHeaderCell>
              <TableHeaderCell>User (UPN)</TableHeaderCell>
              <TableHeaderCell>Reports to</TableHeaderCell>
              <TableHeaderCell>Job</TableHeaderCell>
              <TableHeaderCell>PO limit</TableHeaderCell>
              <TableHeaderCell>Roles</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>No positions yet</TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow
                key={r.key}
                className={styles.row}
                onClick={() => onOpen(r.open)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onOpen(r.open)}
              >
                <TableCell className={styles.nowrap}>
                  <b>{r.title}</b>
                  <span className={styles.sub}>{r.positionId}</span>
                </TableCell>
                <TableCell className={styles.nowrap}>
                  {r.person || (
                    <Badge appearance="tint" color="warning">
                      Vacant
                    </Badge>
                  )}
                  {r.personnelNumber && <span className={styles.sub}>Worker {r.personnelNumber}</span>}
                </TableCell>
                <TableCell className={styles.mono}>
                  {r.upn || '—'}
                  {r.upn && !r.hasUser && (
                    <span className={mergeClasses(styles.sub, styles.missing)}>not in Users</span>
                  )}
                </TableCell>
                <TableCell className={styles.nowrap}>
                  {r.reportsTo}
                  <span className={styles.sub}>{r.reportsToName}</span>
                </TableCell>
                <TableCell className={styles.mono}>{r.jobId}</TableCell>
                <TableCell className={styles.nowrap}>
                  {r.limit === null ? (
                    <span className={styles.missing}>none</span>
                  ) : r.limit === 0 ? (
                    'cannot approve'
                  ) : (
                    usd(r.limit)
                  )}
                </TableCell>
                <TableCell>
                  <span className={styles.roles}>
                    {r.roleIds.length
                      ? r.roleIds.map((id) => (
                          <Badge key={id} appearance="outline" size="small" title={id}>
                            {ROLE_NAME.get(id.toUpperCase()) ?? id}
                          </Badge>
                        ))
                      : '—'}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {unseated.length > 0 && (
        <Caption1>
          Workers without a position:{' '}
          {unseated.map((w) => `${nameOf(w)} (${s(w, 'personnelNumber')})`).join(', ')}. Add an assignment to
          seat them.
        </Caption1>
      )}
    </>
  );
}
